import type { ComponentType } from 'react';

import pageRoutes, { isErpPermission } from '@routes/pageRoutes.ts';
import type { PageRoute } from '@routes/pageRoutes.ts';
import { evaluateRouteAccess } from '@features/cms/components/routeAccess.ts';

import type {
  DashboardNavItem,
  GroupColorTokens,
  GroupedNavItems,
  RegistryContext,
} from './types.ts';

export const GROUP_COLOR_TOKENS: GroupColorTokens = {
  general: { light: 'blue-7', dark: 'blue-8' },
  eshop: { light: 'cyan-7', dark: 'cyan-8' },
  cms: { light: 'purple-6', dark: 'purple-7' },
  erp: { light: 'gold-9', dark: 'gold-10' },
};

const byPriorityThenTitle = (a: DashboardNavItem, b: DashboardNavItem) => {
  const priorityA = a.priority ?? Number.POSITIVE_INFINITY;
  const priorityB = b.priority ?? Number.POSITIVE_INFINITY;

  if (priorityA !== priorityB) {
    return priorityA - priorityB;
  }

  return a.title.localeCompare(b.title, 'es');
};

const toDashboardNavItem = (route: PageRoute): DashboardNavItem | null => {
  if (!route.navGroup || route.enabled === false) {
    return null;
  }

  const Icon: ComponentType = route.icon ?? (() => null);
  const colorToken = route.colorToken ?? GROUP_COLOR_TOKENS[route.navGroup].light;

  return {
    key: route.key,
    title: route.title,
    description: route.description,
    icon: Icon,
    colorToken,
    to: route.path,
    group: route.navGroup,
    featureFlag: route.featureFlag,
    requiredApps: route.requiredApps ? [...route.requiredApps] : undefined,
    requiredAnyApps: route.requiredAnyApps ? [...route.requiredAnyApps] : undefined,
    requiredCapabilities: route.requiredCapabilities ? [...route.requiredCapabilities] : undefined,
    requiredGroups: route.requiredGroups ? [...route.requiredGroups] : undefined,
    requiredPermissions: route.requiredPermissions
      ? [...route.requiredPermissions]
      : undefined,
    requiresStaff: route.requiresStaff,
    priority: route.priority,
    allowSuperuserWithoutApp: route.allowSuperuserWithoutApp,
  } satisfies DashboardNavItem;
};

export const DASHBOARD_NAV_ITEMS: readonly DashboardNavItem[] = pageRoutes
  .map(toDashboardNavItem)
  .filter((item): item is DashboardNavItem => item !== null)
  .sort(byPriorityThenTitle);

export const getVisibleNavItems = (
  items: readonly DashboardNavItem[],
  ctx: RegistryContext,
): DashboardNavItem[] => {
  const permissionSet = new Set<string>(ctx.permissions ?? []);
  const permissionArray = Array.from(permissionSet);
  const hasErpPermission = ctx.isSuperuser || permissionArray.some(isErpPermission);

  return items.filter((item) => {
    const decision = evaluateRouteAccess(
      {
        key: item.key,
        featureFlag: item.featureFlag,
        requiredApps: item.requiredApps ?? [],
        requiredAnyApps: item.requiredAnyApps ?? [],
        requiredCapabilities: item.requiredCapabilities ?? [],
        requiredGroups: item.requiredGroups ?? [],
        requiredPermissions: item.requiredPermissions ?? [],
        requiresStaff: item.requiresStaff !== false,
        navGroup: item.group,
        allowSuperuserWithoutApp: item.allowSuperuserWithoutApp,
      },
      {
        apps: ctx.apps,
        features: ctx.features,
        isStaff: ctx.isStaff,
        isSuperuser: ctx.isSuperuser,
        permissions: permissionArray,
        groups: ctx.groups ?? [],
        roles: ctx.roles ?? [],
        planCapabilities: ctx.planCapabilities,
        requiresDomainPermission: item.group === 'erp',
        domainPermissions: hasErpPermission ? permissionArray : [],
      },
    );

    return decision.allowed;
  });
};

export const groupNavItems = (
  items: readonly DashboardNavItem[],
): GroupedNavItems => {
  return items.reduce<GroupedNavItems>((acc, item) => {
    const groupItems = acc[item.group]?.slice() ?? [];
    groupItems.push(item);
    acc[item.group] = groupItems.sort(byPriorityThenTitle);
    return acc;
  }, {});
};

export const findNavItemByPath = (
  items: readonly DashboardNavItem[],
  rawPathname: string,
  rawSearch?: string,
): DashboardNavItem | undefined => {
  if (!rawPathname) {
    return undefined;
  }

  const sanitizeSearch = (value?: string) => {
    if (!value || value === '?') {
      return '';
    }

    return value.startsWith('?') ? value : `?${value}`;
  };

  const normalize = (value: string) => {
    if (!value) {
      return value;
    }

    const [rawPath = '', ...rawQueryParts] = value.split('?');
    const query = rawQueryParts.join('?');

    let path = rawPath || '/';
    if (!path.startsWith('/')) {
      path = `/${path}`;
    }
    if (path !== '/') {
      path = path.replace(/\/+$/, '');
    }

    return query ? `${path}?${query}` : path;
  };

  const parseValue = (value: string) => {
    const normalized = normalize(value);
    const [pathOnly, ...queryParts] = normalized.split('?');
    const query = queryParts.join('?');

    return {
      normalized,
      pathOnly,
      query,
      params: new URLSearchParams(query),
    } as const;
  };

  const search = sanitizeSearch(rawSearch);
  const base = parseValue(rawPathname);
  const withSearch = search ? parseValue(`${rawPathname}${search}`) : undefined;

  let bestMatch: DashboardNavItem | undefined;
  let bestScore = -1;

  for (const item of items) {
    const parsedItem = parseValue(item.to);

    if (withSearch) {
      if (parsedItem.normalized === withSearch.normalized) {
        return item;
      }

      const isSamePath = parsedItem.pathOnly === withSearch.pathOnly;
      const itemHasQuery = Boolean(parsedItem.query);

      if (isSamePath && itemHasQuery) {
        let matchesSubset = true;
        parsedItem.params.forEach((value, key) => {
          if (!withSearch.params.has(key)) {
            matchesSubset = false;
            return;
          }

          const allValues = withSearch.params.getAll(key);
          if (!allValues.includes(value)) {
            matchesSubset = false;
          }
        });

        if (matchesSubset) {
          return item;
        }
      }
    }

    const candidatePath = withSearch?.pathOnly ?? base.pathOnly;

    if (parsedItem.pathOnly === candidatePath) {
      const score = parsedItem.normalized.length;
      if (score > bestScore) {
        bestMatch = item;
        bestScore = score;
      }
      continue;
    }

    if (base.pathOnly.startsWith(`${parsedItem.pathOnly}/`)) {
      const score = parsedItem.pathOnly.length;
      if (score > bestScore) {
        bestMatch = item;
        bestScore = score;
      }
    }
  }

  return bestMatch;
};
